import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getAuthToken } from '../client'

/**
 * Phase 35-02 (DRAFT-01): client integration for the draft-scoped chat
 * endpoint introduced in Phase 35-01.
 *
 * Wire contract (matches API SUMMARY 35-01):
 *   POST {VITE_API_URL}/api/v1/agents/:agentId/drafts/:draftId/messages
 *   Authorization: Bearer <jwt>
 *   Content-Type: application/json
 *   Accept: text/event-stream
 *   Body: { content }
 *
 * SSE events (server emits exactly these three):
 *   event: token         data: { content: string }              -- streamed tokens
 *   event: draftUpdated  data: { draftId, content }             -- only when an edit applied
 *   event: done          data: { messageId, draftId, draftUpdated }
 *
 * The hook does NOT trust `event.draftUpdated.content` directly. It only
 * triggers a TanStack Query invalidation against the draft detail key, so
 * the body editor re-renders from the authenticated GET draft path.
 *
 * Imported (Drive/ClickUp) drafts are unaffected — the chat endpoint is
 * scoped to draft chat, which is independent of source provenance.
 */

export interface DraftChatRole {
  role: 'user' | 'assistant'
  content: string
}

export interface SendDraftMessageResult {
  messageId?: string
  draftId?: string
  draftUpdated: boolean
}

export interface SendDraftMessageOptions {
  signal?: AbortSignal
}

interface DraftSSEEvent {
  event: string
  data: unknown
}

/**
 * Open an authenticated SSE connection to the draft-chat endpoint.
 * Mirrors the streamChat pattern in sse-client.ts but targets the
 * draft-scoped route and yields the draft-chat event shapes.
 */
export async function* streamDraftChat(params: {
  agentId: string
  draftId: string
  content: string
  signal?: AbortSignal
}): AsyncGenerator<DraftSSEEvent, void, undefined> {
  const baseUrl = import.meta.env.VITE_API_URL
  const token = await getAuthToken()

  const response = await fetch(
    `${baseUrl}/api/v1/agents/${encodeURIComponent(params.agentId)}/drafts/${encodeURIComponent(params.draftId)}/messages`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content: params.content }),
      signal: params.signal,
    },
  )

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    throw new Error(
      `Draft chat SSE failed: ${response.status} ${errorText}`,
    )
  }

  if (!response.body) {
    throw new Error('Draft chat SSE response has no body')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      // Normalize CRLF -> LF (some proxies/servers emit \r\n)
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n').replace(/\r/g, '\n')

      // SSE events are separated by double newlines
      const events = buffer.split('\n\n')
      buffer = events.pop() ?? ''

      for (const eventText of events) {
        if (!eventText.trim()) continue

        let eventType = ''
        const dataLines: string[] = []

        for (const line of eventText.split('\n')) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            const v = line.slice(5)
            dataLines.push(v.startsWith(' ') ? v.slice(1) : v)
          }
        }

        const eventData = dataLines.join('\n').trim()
        if (eventType && eventData) {
          try {
            const parsed = JSON.parse(eventData)
            yield { event: eventType, data: parsed }
          } catch (e) {
            console.warn('[DraftChat SSE] Failed to parse event data:', eventType, eventData, e)
          }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export interface UseDraftChatResult {
  /** Submit a user message; resolves once the SSE stream completes. */
  sendMessage: (content: string) => Promise<SendDraftMessageResult>
  /** Streamed assistant tokens for the in-flight turn (resets on each send). */
  streamingText: string
  /** True between submit and `done` event. */
  isStreaming: boolean
  /** Last error message (cleared on next send). */
  error: string | null
  /**
   * Local-session message thread. Persistence is server-side; the panel
   * shows what was exchanged in the current session. A future plan can
   * replay history once a GET endpoint is added (35-01 SUMMARY notes
   * "messages persisted, replayable later").
   */
  messages: DraftChatRole[]
}

/**
 * Cross-repo client integration for the draft-scoped chat endpoint.
 * Returns send/state for a `DraftChatPanel` to render. On `draftUpdated`
 * the draft detail TanStack query is invalidated so the body editor
 * re-renders from the authenticated GET draft path.
 */
export function useDraftChat(params: { agentId: string; draftId: string }): UseDraftChatResult {
  const { agentId, draftId } = params
  const queryClient = useQueryClient()

  const [streamingText, setStreamingText] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [messages, setMessages] = useState<DraftChatRole[]>([])

  // Track latest streaming text via ref so the resolver can capture it
  // when committing the assistant turn to the thread on `done`.
  const streamingTextRef = useRef('')

  const sendMessage = useCallback(
    async (content: string): Promise<SendDraftMessageResult> => {
      setError(null)
      setIsStreaming(true)
      setStreamingText('')
      streamingTextRef.current = ''

      // Optimistically append the user turn to the local thread.
      setMessages((prev) => [...prev, { role: 'user', content }])

      let result: SendDraftMessageResult = { draftUpdated: false }

      try {
        for await (const event of streamDraftChat({ agentId, draftId, content })) {
          if (event.event === 'token') {
            const data = event.data as { content?: string }
            if (typeof data.content === 'string') {
              streamingTextRef.current += data.content
              setStreamingText(streamingTextRef.current)
            }
          } else if (event.event === 'draftUpdated') {
            const data = event.data as { draftId?: string; content?: string }
            // Don't trust `data.content` — invalidate so the editor re-fetches
            // the authoritative body from GET /agents/:id/drafts/:draftId.
            queryClient.invalidateQueries({
              queryKey: ['drafts', 'detail', agentId, data.draftId ?? draftId],
            })
            result.draftUpdated = true
          } else if (event.event === 'done') {
            const data = event.data as {
              messageId?: string
              draftId?: string
              draftUpdated?: boolean
            }
            result = {
              messageId: data.messageId,
              draftId: data.draftId,
              draftUpdated: Boolean(data.draftUpdated) || result.draftUpdated,
            }
          } else if (event.event === 'error') {
            const data = event.data as { detail?: string; message?: string }
            throw new Error(data.detail ?? data.message ?? 'Draft chat stream error')
          }
        }

        // Commit the assistant turn to the local thread once the stream ends.
        if (streamingTextRef.current) {
          const assistantText = streamingTextRef.current
          setMessages((prev) => [...prev, { role: 'assistant', content: assistantText }])
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send message'
        setError(message)
        // Roll back the optimistic user turn so the thread does not show a
        // stuck user message without an assistant reply.
        setMessages((prev) => (prev[prev.length - 1]?.role === 'user' ? prev.slice(0, -1) : prev))
        throw err
      } finally {
        setIsStreaming(false)
        setStreamingText('')
        streamingTextRef.current = ''
      }

      return result
    },
    [agentId, draftId, queryClient],
  )

  return { sendMessage, streamingText, isStreaming, error, messages }
}
